<script setup lang="ts">
import ContextMenu from '@/components/ContextMenu/ContextMenu.vue';
import { info, warning } from '@/components/icons/icons.ts';
import { Tab, TabIndicatorState } from '@/components/Tabs/types.ts';
import { computed, toValue } from 'vue';

const props = defineProps<{
  value: string;
  tab: Tab;
}>();

const indicatorState = computed(() => toValue(props.tab.indicatorState));
</script>

<template>
  <v-tab
    :value="value"
    :disabled="tab.disabled"
    :prependIcon="tab.icon"
    :sliderColor="indicatorState ?? TabIndicatorState.DEFAULT"
  >
    <span :class="{ [`text-${indicatorState}`]: indicatorState }">
      {{ tab.title }}
      <v-icon
        v-if="indicatorState"
        class="tab-indicator-icon"
        :icon="indicatorState === TabIndicatorState.INFO ? info : warning"
        size="small"
        :class="{
          'text-info': indicatorState === TabIndicatorState.INFO,
        }"
      />
    </span>
    <ContextMenu v-if="tab.contextMenuActions" :actions="tab.contextMenuActions" />
  </v-tab>
</template>

<style lang="scss" scoped>
@use '@/styles/variables';
@use '@/styles/vuetifySettings';

.tab-indicator-icon {
  vertical-align: top;
  margin-top: -2px;
  margin-left: 4px;
}

:deep(.v-btn__prepend) {
  margin-inline: calc(var(--v-btn-height) / -9) calc(var(--v-btn-height) / 9);
}

.v-btn {
  color: rgb(var(--v-theme-secondary));

  &:hover {
    color: rgb(var(--v-theme-secondary-darken-2));

    &:deep(> .v-btn__overlay) {
      opacity: 0;
    }
  }

  &.v-tab--selected {
    color: rgb(var(--v-theme-on-surface));
  }

  &.v-btn--disabled {
    color: rgb(var(--v-theme-primary-lighten-2));
    opacity: 1;
  }
}
</style>
